import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { ENV } from '@app/constants/env';
import { getToken } from '@app/helpers/authProvider';
import { UserCommentEntity, UserCommentRequest } from '@app/types/user.comments.type';


const rawBaseQuery = fetchBaseQuery({
    baseUrl: ENV.BASE_API_URL + ENV.TEMPLATES_APIURL,
});

const authBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    incomingArgs,
    api,
    extraOptions
) => {
    let args: string | FetchArgs = incomingArgs;

    if (typeof args === 'string') {
        args = { url: args };
    }

    try {
        const token = await getToken();

        if (token) {
            const fetchArgs = args as FetchArgs;
            const existingHeaders = fetchArgs.headers || {};

            const headers =
                existingHeaders instanceof Headers
                    ? existingHeaders
                    : new Headers(existingHeaders as HeadersInit);

            headers.set('Authorization', `Bearer ${token}`);

            fetchArgs.headers = headers;
        }

        return await rawBaseQuery(args, api, extraOptions);
    } catch (error) {
        console.error('Error in authBaseQuery:', error);

        return {
            error: {
                status: 'CUSTOM_ERROR',
                error: String(error),
            } as FetchBaseQueryError,
        };
    }
};

export const userCommentsApi = createApi({
    reducerPath: 'userCommentsApi',
    baseQuery: authBaseQuery,
    tagTypes: ['UserComments'],

    endpoints: (builder) => ({

        getUserComments: builder.query<UserCommentEntity[], UserCommentRequest>({
            query: (requestbody) => ({
                url: `Comments/GetUserComments`,
                method: 'POST',
                body: requestbody,
            }),
            providesTags: ['UserComments'],
        }),

        getUserCommentById: builder.query<UserCommentEntity, number>({
            query: (id) => ({
                url: `Comments/GetUserCommentById/${id}`,
            }),
            providesTags: ['UserComments'],
        }),

        createUserComment: builder.mutation<number, Partial<UserCommentEntity>>({
            query: (comment) => ({
                url: `Comments/CreateUserComment`,
                method: 'POST',
                body: comment,
            }),
            invalidatesTags: ['UserComments'],
        }),

        updateUserComment: builder.mutation<boolean, UserCommentEntity>({
            query: (comment) => ({
                url: `Comments/UpdateUserComment`,
                method: 'PUT',
                body: comment,
            }),
            invalidatesTags: ['UserComments'],
        }),

        deleteUserComment: builder.mutation<boolean, { id: number; modifiedBy: number }>({
            query: ({ id, modifiedBy }) => ({
                url: `Comments/DeleteUserComment/${id}/${modifiedBy}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['UserComments'],
        }),

    }),
});

export const {
    useGetUserCommentsQuery,
    useGetUserCommentByIdQuery,
    useCreateUserCommentMutation,
    useUpdateUserCommentMutation,
    useDeleteUserCommentMutation,
} = userCommentsApi;